/**
 * RpgDevelopPage (ТЗ №5, «Развитие») — a tabbed hub for character progression:
 *
 *  - Инвентарь — owned items + quantities (``GET /api/rpg/inventory/``).
 *  - Оружие — catalog with own/equip/upgrade (``weapons`` + equip / upgrade-weapon).
 *  - Прокачка — raise hp / damage for mora (``upgrade-stat``).
 *  - Алхимия — recipes + craft (``alchemy`` + ``alchemy/craft``).
 *  - Магазин — in-game shop, buy for mora (``shop`` + ``shop/buy``).
 *
 * Every cost / material / limit comes from the server; after a write we refetch the
 * affected list + the profile so balances and prices stay authoritative.
 */
import { useState } from "react";

import { CurrencyIcon } from "@melusine/shared-ui";
import { useBackButton } from "@melusine/shared-telegram";
import {
  STAT_LABELS,
  useRpgAlchemy,
  useRpgInventory,
  useRpgProfile,
  useRpgShop,
  useRpgWeapons,
  type RpgInventoryItem,
  type RpgProfile,
  type RpgRecipe,
  type RpgShopItem,
  type RpgWeapon,
  type UpgradeStat,
} from "@entities/rpg";
import {
  rpgErrorMessage,
  useBuyMutation,
  useCraftMutation,
  useEquipWeaponMutation,
  useUpgradeStatMutation,
  useUpgradeWeaponMutation,
} from "@features/rpg";
import { StatePanel } from "@widgets/catalog-browser";
import { useToast } from "@shared/ui/toast";
import { formatUnits } from "@shared/lib/format";
import { routes, useNavigate, type RpgDevelopTab } from "@shared/router";

import { RpgHeader } from "./ui/RpgHeader";
import { RpgIcon } from "./ui/RpgIcon";
import styles from "./rpg.module.css";

const TABS: readonly { readonly key: RpgDevelopTab; readonly label: string }[] = [
  { key: "inventory", label: "Инвентарь" },
  { key: "weapons", label: "Оружие" },
  { key: "upgrade", label: "Прокачка" },
  { key: "alchemy", label: "Алхимия" },
  { key: "shop", label: "Магазин" },
];

export interface RpgDevelopPageProps {
  readonly initialTab?: RpgDevelopTab;
}

export function RpgDevelopPage({
  initialTab,
}: RpgDevelopPageProps): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const [tab, setTab] = useState<RpgDevelopTab>(initialTab ?? "weapons");

  const profileQuery = useRpgProfile();
  const inventoryQuery = useRpgInventory();
  const weaponsQuery = useRpgWeapons();
  const alchemyQuery = useRpgAlchemy();
  const shopQuery = useRpgShop();

  const equip = useEquipWeaponMutation();
  const upgradeWeapon = useUpgradeWeaponMutation();
  const upgradeStat = useUpgradeStatMutation();
  const craft = useCraftMutation();
  const buy = useBuyMutation();

  useBackButton(() => {
    navigate(routes.rpg());
  });

  const busy =
    equip.isLoading ||
    upgradeWeapon.isLoading ||
    upgradeStat.isLoading ||
    craft.isLoading ||
    buy.isLoading;

  const run = async (
    action: () => Promise<unknown>,
    success: string,
    after: () => void,
  ): Promise<void> => {
    if (busy) {
      return;
    }
    try {
      await action();
      toast.show(success, "success");
      profileQuery.refetch();
      after();
    } catch (err) {
      toast.show(rpgErrorMessage(err), "default");
    }
  };

  return (
    <div className={styles.page}>
      <RpgHeader
        title="Развитие"
        onBack={() => navigate(routes.rpg())}
        {...(profileQuery.data ? { mora: profileQuery.data.mora } : {})}
      />

      <div className={styles.tabBar} role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            className={`${styles.tab} ${tab === t.key ? styles.tabActive : ""}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "inventory" ? (
        <InventoryTab query={inventoryQuery} />
      ) : null}

      {tab === "weapons" ? (
        <WeaponsTab
          query={weaponsQuery}
          busy={busy}
          onEquip={(w) =>
            void run(() => equip.mutate(w.id), "Оружие экипировано.", () =>
              weaponsQuery.refetch(),
            )
          }
          onUpgrade={(w) =>
            void run(
              () => upgradeWeapon.mutate(w.id),
              "Оружие улучшено.",
              () => weaponsQuery.refetch(),
            )
          }
        />
      ) : null}

      {tab === "upgrade" ? (
        <UpgradeTab
          profile={profileQuery.data}
          busy={busy}
          onUpgrade={(stat) =>
            void run(
              () => upgradeStat.mutate(stat),
              "Характеристика улучшена.",
              () => undefined,
            )
          }
        />
      ) : null}

      {tab === "alchemy" ? (
        <AlchemyTab
          query={alchemyQuery}
          busy={busy}
          onCraft={(r) =>
            void run(() => craft.mutate(r.id), "Предмет создан.", () => {
              alchemyQuery.refetch();
              inventoryQuery.refetch();
            })
          }
        />
      ) : null}

      {tab === "shop" ? (
        <ShopTab
          query={shopQuery}
          busy={busy}
          onBuy={(s) =>
            void run(() => buy.mutate(s.id), "Покупка совершена.", () => {
              shopQuery.refetch();
              inventoryQuery.refetch();
            })
          }
        />
      ) : null}
    </div>
  );
}

/* ---------------- Inventory ---------------- */
function InventoryTab({
  query,
}: {
  readonly query: ReturnType<typeof useRpgInventory>;
}): JSX.Element {
  if (query.isLoading) {
    return <StatePanel kind="loading" />;
  }
  if (query.error && query.data === null) {
    return (
      <StatePanel kind="error" message="Не удалось загрузить." onRetry={query.refetch} />
    );
  }
  const items = query.data ?? [];
  if (items.length === 0) {
    return <StatePanel kind="empty" message="Инвентарь пуст." />;
  }
  return (
    <div className={styles.list}>
      {items.map((item: RpgInventoryItem) => (
        <div key={item.item_id} className={styles.row}>
          <RpgIcon url={item.icon_url} name={item.name} size={48} />
          <div className={styles.rowBody}>
            <div className={styles.rowNameRow}>
              <span className={styles.rowName}>{item.name}</span>
              {item.rarity ? <span className={styles.tag}>{item.rarity}</span> : null}
            </div>
            {item.description ? (
              <div className={styles.metaLine}>{item.description}</div>
            ) : null}
          </div>
          <span className={styles.qtyBadge}>×{formatUnits(item.qty)}</span>
        </div>
      ))}
    </div>
  );
}

/* ---------------- Weapons ---------------- */
function WeaponsTab({
  query,
  busy,
  onEquip,
  onUpgrade,
}: {
  readonly query: ReturnType<typeof useRpgWeapons>;
  readonly busy: boolean;
  readonly onEquip: (w: RpgWeapon) => void;
  readonly onUpgrade: (w: RpgWeapon) => void;
}): JSX.Element {
  if (query.isLoading) {
    return <StatePanel kind="loading" />;
  }
  if (query.error && query.data === null) {
    return (
      <StatePanel kind="error" message="Не удалось загрузить." onRetry={query.refetch} />
    );
  }
  const weapons = query.data ?? [];
  if (weapons.length === 0) {
    return <StatePanel kind="empty" message="Оружие недоступно." />;
  }
  return (
    <div className={styles.list}>
      {weapons.map((w) => (
        <div key={w.id} className={styles.row}>
          <RpgIcon url={w.icon_url} name={w.name} size={48} />
          <div className={styles.rowBody}>
            <div className={styles.rowNameRow}>
              <span className={styles.rowName}>{w.name}</span>
              {w.rarity ? <span className={styles.tag}>{w.rarity}</span> : null}
            </div>
            <div className={styles.metaLine}>
              <span>
                Урон {formatUnits(w.owned ? w.effective_damage : w.base_damage)}
              </span>
              {w.owned ? <span>Ур. {w.owned_level}</span> : null}
            </div>
            {w.owned ? (
              <div className={styles.reward}>
                Улучшение: {formatUnits(w.upgrade_cost_mora)} моры
                {w.upgrade_materials.length > 0
                  ? ` + ${w.upgrade_materials
                      .map((m) => `${m.name}×${m.qty}`)
                      .join(", ")}`
                  : ""}
              </div>
            ) : (
              <div className={styles.muted}>Не получено</div>
            )}
          </div>
          <div className={styles.colBtns}>
            {w.equipped ? (
              <span className={styles.equippedTag}>Экипировано</span>
            ) : w.owned ? (
              <button
                type="button"
                className={`${styles.actionBtn} ${styles.actionBtnGlitter}`}
                disabled={busy}
                onClick={() => onEquip(w)}
              >
                Надеть
              </button>
            ) : null}
            {w.owned ? (
              <button
                type="button"
                className={styles.actionBtn}
                disabled={busy}
                onClick={() => onUpgrade(w)}
              >
                Улучшить
              </button>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------------- Upgrade (stats) ---------------- */
function UpgradeTab({
  profile,
  busy,
  onUpgrade,
}: {
  readonly profile: RpgProfile | null;
  readonly busy: boolean;
  readonly onUpgrade: (stat: UpgradeStat) => void;
}): JSX.Element {
  if (profile === null) {
    return <StatePanel kind="loading" />;
  }
  const rows: readonly {
    readonly stat: UpgradeStat;
    readonly value: string;
    readonly cost: number;
    readonly gain: number;
  }[] = [
    {
      stat: "hp",
      value: `${formatUnits(profile.hp)} / ${formatUnits(profile.max_hp)}`,
      cost: profile.upgrade_hp_cost,
      gain: profile.upgrade_hp_gain,
    },
    {
      stat: "attack",
      value: formatUnits(profile.effective_damage),
      cost: profile.upgrade_attack_cost,
      gain: profile.upgrade_attack_gain,
    },
  ];
  return (
    <div className={styles.list}>
      {rows.map((row) => (
        <div key={row.stat} className={styles.row}>
          <div className={styles.rowBody}>
            <div className={styles.rowName}>{STAT_LABELS[row.stat]}</div>
            <div className={styles.metaLine}>Текущее: {row.value}</div>
            <div className={styles.reward}>
              Улучшение: {formatUnits(row.cost)} моры
              {row.gain > 0 ? ` · +${formatUnits(row.gain)}` : ""}
            </div>
          </div>
          <button
            type="button"
            className={styles.actionBtn}
            disabled={busy}
            onClick={() => onUpgrade(row.stat)}
          >
            Улучшить
          </button>
        </div>
      ))}
      <div className={styles.muted}>
        Улучшение списывает мору по серверной цене. Доступно: {formatUnits(profile.mora)}{" "}
        моры.
      </div>
    </div>
  );
}

/* ---------------- Alchemy ---------------- */
function AlchemyTab({
  query,
  busy,
  onCraft,
}: {
  readonly query: ReturnType<typeof useRpgAlchemy>;
  readonly busy: boolean;
  readonly onCraft: (r: RpgRecipe) => void;
}): JSX.Element {
  if (query.isLoading) {
    return <StatePanel kind="loading" />;
  }
  if (query.error && query.data === null) {
    return (
      <StatePanel kind="error" message="Не удалось загрузить." onRetry={query.refetch} />
    );
  }
  const recipes = query.data ?? [];
  if (recipes.length === 0) {
    return <StatePanel kind="empty" message="Рецепты недоступны." />;
  }
  return (
    <div className={styles.list}>
      {recipes.map((r) => (
        <div key={r.id} className={styles.row}>
          <RpgIcon url={r.result_icon_url} name={r.result_name} size={48} />
          <div className={styles.rowBody}>
            <div className={styles.rowNameRow}>
              <span className={styles.rowName}>{r.result_name}</span>
              <span className={styles.tag}>×{r.result_qty}</span>
              {r.required_rank > 0 ? (
                <span className={styles.tag}>ранг {r.required_rank}</span>
              ) : null}
            </div>
            <div className={styles.metaLine}>
              {r.inputs.map((i) => `${i.name}×${i.qty}`).join(", ") || "Без материалов"}
            </div>
            {r.cost_mora > 0 ? (
              <div className={styles.reward}>
                Стоимость: {formatUnits(r.cost_mora)} моры
              </div>
            ) : null}
          </div>
          <button
            type="button"
            className={`${styles.actionBtn} ${styles.actionBtnGlitter}`}
            disabled={busy}
            onClick={() => onCraft(r)}
          >
            Создать
          </button>
        </div>
      ))}
    </div>
  );
}

/* ---------------- Shop ---------------- */
function ShopTab({
  query,
  busy,
  onBuy,
}: {
  readonly query: ReturnType<typeof useRpgShop>;
  readonly busy: boolean;
  readonly onBuy: (s: RpgShopItem) => void;
}): JSX.Element {
  if (query.isLoading) {
    return <StatePanel kind="loading" />;
  }
  if (query.error && query.data === null) {
    return (
      <StatePanel kind="error" message="Не удалось загрузить." onRetry={query.refetch} />
    );
  }
  const items = query.data ?? [];
  if (items.length === 0) {
    return <StatePanel kind="empty" message="Магазин пуст." />;
  }
  return (
    <div className={styles.list}>
      {items.map((s) => (
        <div key={s.id} className={styles.row}>
          <RpgIcon url={s.icon_url} name={s.name} size={48} />
          <div className={styles.rowBody}>
            <div className={styles.rowNameRow}>
              <span className={styles.rowName}>{s.name}</span>
              {s.purchase_limit > 0 ? (
                <span className={styles.tag}>
                  {s.purchased}/{s.purchase_limit}
                </span>
              ) : null}
            </div>
            <div className={styles.reward}>
              <CurrencyIcon kind="mora" size={12} /> {formatUnits(s.price_mora)}
            </div>
          </div>
          <button
            type="button"
            className={styles.actionBtn}
            disabled={busy || s.sold_out}
            onClick={() => onBuy(s)}
          >
            {s.sold_out ? "Распродано" : "Купить"}
          </button>
        </div>
      ))}
    </div>
  );
}
